import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useConfig } from '../../../config/ConfigProvider';
import { captureException } from '../../../services/sentryService';
import { ContainerType } from '../../../types';
import { messageOfUnknownError } from '../../../utils/utils';
import HtmlEmbeddedAlert from '../../Banners/HtmlEmbeddedAlert';
import BraintreeCreditCardForm, {
  BraintreeCreditCardFormHandle,
  TokenizeCardResult,
} from '../../CreditCard/Braintree/BraintreeCreditCardForm';
import { useCreditcardService } from '../../CreditCardsMutationProvider';
import { OverlayIds, useOverlays } from '../../OverlaysProvider';

import BaseModal from './BaseModal';

type NewCardModalProps = {
  containerType: ContainerType;
};

/**
 * Modal holding the Braintree credit card form.
 */
const NewCardModal: React.FC<NewCardModalProps> = ({ containerType }) => {
  const formHandle = useRef<BraintreeCreditCardFormHandle>(null);
  const [addCardError, setAddCardError] = useState<string>();
  const [addCardLoading, setAddCardLoading] = useState(false);
  const { hideOverlay } = useOverlays();
  const {
    config: { env },
  } = useConfig();
  const isLocalEnv = env === 'local';
  const { t } = useTranslation();
  const {
    saveCreditCard,
    data: { btToken = '' },
  } = useCreditcardService();

  const tokenizeAndSaveCard = async () => {
    if (!formHandle.current) {
      captureException({ message: 'braintree_form_handle_not_found' });
      throw new Error(t('checkout.payment.creditcard.error.tokenization.retry'));
    }
    let tokenizationResult: TokenizeCardResult;
    try {
      tokenizationResult = await formHandle.current?.tokenizeCard();
    } catch (error: any) {
      captureException(error);
      throw new Error(t('checkout.payment.creditcard.error.tokenization.retry'));
    }
    return await saveCreditCard(tokenizationResult);
  };

  const handleAddCardSubmit = () => {
    setAddCardLoading(true);
    setAddCardError(undefined);

    tokenizeAndSaveCard()
      .then((result) => {
        setAddCardLoading(false);
        if (isLocalEnv && result) console.log('Should be redirected to preview page');
      })
      .catch((error: unknown) => {
        setAddCardLoading(false);
        setAddCardError(messageOfUnknownError(error));
      });
  };

  const handleFatalError = useCallback(() => {
    hideOverlay(OverlayIds.ADD_NEW_CARD);
  }, [hideOverlay]);

  return (
    <BaseModal
      modalId={OverlayIds.ADD_NEW_CARD}
      handleSubmit={handleAddCardSubmit}
      heading={t('checkout.payment.creditcard.addnew')}
      primaryButtonLabel={t('general.button.next')}
      headingId="add-card"
      closeBtnAriaLabel="Close"
      dataZta="modal-addCard"
      size="md"
      isLoading={addCardLoading}>
      <HtmlEmbeddedAlert innerHtml={t('checkout.payment.creditcard.3d.secure.message')} />
      <BraintreeCreditCardForm
        token={btToken}
        containerType={containerType}
        error={addCardError}
        onFatalError={handleFatalError}
        allowVaulting={true}
        ref={formHandle}
        labelsInline
      />
    </BaseModal>
  );
};

export default NewCardModal;
